import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { DropdownObject } from '@inmoment/the-kitchen';

import { WidgetInsightFeedSource } from 'app/modules/widget/components/widget-insight-feed/widget-insight-feed.component';
import { SystemService } from 'app/services/system.service';

import {
  RealtimeConnection,
  RealtimeEventHandler,
  RealtimeMessage,
  RealtimeService,
} from '../realtime.service';
import { AiInsight } from '../semantria-api/ai-insights.service';
import { GroupService } from '../semantria-api/group.service';
import { SideloadedStorageListResult, StorageAPIService } from '../storage-api.service';
import { ObjectLabel, Sideloadable } from './object-label.service';

export interface AiInsightFeedParams {
  id?: string;
  name: string;
  projectId: string;
  dashboardId?: string;
  insightQuery: { properties: string[]; tags: string[] };
  objectLabels?: string[];
}

export type AiInsightFeedInsight = Omit<AiInsight, 'properties'> & {
  properties: string[];
};

export interface AiInsightFeed {
  accountId: string;
  createdAt: string;
  dashboardId?: string;
  description?: string;
  id: string;
  insightQuery: AiInsightFeedInsight;
  modifiedAt?: string;
  name: string;
  projectId?: string;
  userId: string;
  objectLabels?: string[] | ObjectLabel[];
  roles?: string[];
}

@Injectable({ providedIn: 'root' })
export class AiInsightsGroupFeedService extends StorageAPIService {
  constructor(
    http: HttpClient,
    systemService: SystemService,
    private groupService: GroupService,
    private realtimeService: RealtimeService
  ) {
    super(http, systemService);
  }

  static map(feed: AiInsightFeed, objectLabels: ObjectLabel[]): AiInsightFeed {
    feed.objectLabels = this.mapObjectLabels(feed, objectLabels);
    return feed;
  }

  on(namespace: string, fn: RealtimeEventHandler<AiInsightFeed>): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        feed: (data: RealtimeMessage<AiInsightFeed>) => {
          fn(data);
        },
      },
      'aiInsightsGroupFeedService'
    );
  }

  getAiInsightsGroupFeeds(
    feedSource: WidgetInsightFeedSource,
    projectId: string,
    dashboardId?: string,
    queryIds?: string[]
  ): Promise<SideloadedStorageListResult<AiInsightFeed>> {
    const groupId = this.groupService.getSelectedGroupId();
    const urlPath = `/groups/${groupId}/feeds`;
    let query = '';
    query = queryIds?.join(' OR ');
    const params: any = {
      projectId,
      sideloads: 'objectLabels',
      query: query || null,
    };

    if (feedSource !== 'allProjectFeeds') {
      params.dashboardId = dashboardId;
    }

    return this.get<SideloadedStorageListResult<AiInsightFeed>>(urlPath, params)
      .then((result) => {
        result.items = result.items?.map((feed) =>
          AiInsightsGroupFeedService.map(feed, result.sideloaded.objectLabels)
        );
        return result;
      })
      .catch((error) => {
        throw error;
      });
  }

  getAiInsightsFeed(feedId: string) {
    const groupId = this.groupService.getSelectedGroupId();

    const urlPath = `/groups/${groupId}/feeds/${feedId}`;
    return this.get<Sideloadable<AiInsightFeed>>(urlPath, { sideloads: 'objectLabels' })
      .then((result) => AiInsightsGroupFeedService.map(result, result.sideloaded.objectLabels))
      .catch((error) => {
        throw error;
      });
  }

  getAiInsightsFeedsDetailsForSelectedFeed(feedId: string) {
    const groupId = this.groupService.getSelectedGroupId();

    const urlPath = `/groups/${groupId}/feeds/${feedId}/insights`;
    return this.get<AiInsight[]>(urlPath).catch((error) => {
      throw error;
    });
  }

  getInsightFeedsDropdownObject(feed: AiInsightFeed): DropdownObject {
    return {
      id: feed.id,
      title: feed.name ?? feed.id,
    };
  }

  getInsightFeedsDropdownObjectList(feedsList: AiInsightFeed[] = []): DropdownObject[] {
    return feedsList
      .sort((feedA, feedB) => {
        const titleA = feedA.name ?? feedA.id;
        const titleB = feedB.name ?? feedB.id;

        return titleA.localeCompare(titleB, undefined, { sensitivity: 'base' });
      })
      .map((feed) => ({
        id: feed.id,
        title: feed.name ?? feed.id,
      }));
  }

  getInsightFeedDropdownState(
    feeds: AiInsightFeed[] = [],
    savedSelection?: string
  ): { options: DropdownObject[]; selectedValue?: string } {
    const dropdownOptions = this.getInsightFeedsDropdownObjectList(feeds ?? []);

    if (!feeds?.length) {
      return {
        options: dropdownOptions,
        selectedValue: undefined,
      };
    }

    const hasSavedSelection =
      !!savedSelection && dropdownOptions.some((feed) => feed.id === savedSelection);

    const selectedValue = hasSavedSelection ? savedSelection : dropdownOptions[0]?.id;

    return {
      options: dropdownOptions,
      selectedValue,
    };
  }

  createAiInsightsGroupFeed(params: AiInsightFeedParams) {
    const groupId = this.groupService.getSelectedGroupId();
    const urlPath = `/groups/${groupId}/feeds`;
    return this.post<AiInsightFeed>(urlPath, params);
  }

  updateAiInsightsGroupFeed(feedId: string, params: AiInsightFeedParams) {
    const groupId = this.groupService.getSelectedGroupId();
    const urlPath = `/groups/${groupId}/feeds/${feedId}`;
    return this.patch<AiInsightFeed>(urlPath, params);
  }

  deleteAiInsightsGroupFeed(feedId: string) {
    const groupId = this.groupService.getSelectedGroupId();
    const urlPath = `/groups/${groupId}/feeds/${feedId}`;
    return this.delete<AiInsightFeed>(urlPath);
  }

  async updateFeedsDashboardId(feedIds: string[], newDashboardId: string) {
    if (!feedIds || feedIds.length === 0) {
      return;
    }

    try {
      const updatePromises = feedIds.map((feedId) => {
        const updatedParams: Partial<AiInsightFeedParams> = {
          dashboardId: newDashboardId,
        };
        return this.updateAiInsightsGroupFeed(feedId, updatedParams as AiInsightFeedParams);
      });

      await Promise.all(updatePromises);
    } catch (error) {
      throw error;
    }
  }
}
